import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchAiNlpPropertiesThunk,
  endChatSessionThunk,
  // fetchChatHistoryThunk,
  loadChatSessionThunk,
} from "../store/thunks/aiNlpThunk";
import {
  clearConversation,
  setUserLocation,
  setLocationPermission,
  setSessionId,
} from "../store/slices/aiNlpSlice";
import { sendUserActivityLog } from "../store/thunks/userActivityLogThunk";
import PropertyGridTile from "./properties/PropertyGridTile";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  MicrophoneIcon,
  SparklesIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import "../styles/AIChat.css";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

const Message = ({ content, role, properties, error }) => {
  if (role === "user") {
    return (
      <motion.div
        className="message user-message"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.2 }}>
        {content}
      </motion.div>
    );
  }

  return (
    <motion.div
      className="message ai-message"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}>
      <div className={error ? "text-red-500" : ""}>{content}</div>
      {properties && properties.listings && properties.listings.length > 0 && (
        <motion.div
          className="property-grid"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}>
          {properties.listings.map((property, index) => (
            <PropertyGridTile
              key={`${property.mlsNumber}-${index}`}
              property={property}
              target="_blank"
            />
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

// const ChatHistoryModal = ({ isOpen, onClose, chatHistory, onSelectChat }) => {
//   if (!isOpen) return null;

//   return (
//     <motion.div
//       className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}>
//       <motion.div
//         className="bg-white p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
//         initial={{ scale: 0.9 }}
//         animate={{ scale: 1 }}>
//         <div className="flex justify-between items-center mb-4">
//           <h2 className="text-xl font-semibold">Chat History</h2>
//           <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
//             <XMarkIcon className="h-6 w-6" />
//           </button>
//         </div>
//         <div className="space-y-4">
//           {chatHistory.map((chat) => (
//             <motion.div
//               key={chat.sessionId}
//               className="border p-4 cursor-pointer hover:bg-gray-50"
//               whileHover={{ scale: 1.01 }}
//               onClick={() => onSelectChat(chat.sessionId)}>
//               <div className="flex justify-between items-start">
//                 <div>
//                   <p className="font-medium">{chat.summary || "Chat Session"}</p>
//                   <p className="text-sm text-gray-500">
//                     {new Date(chat.createdAt).toLocaleDateString()}
//                   </p>
//                 </div>
//                 <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
//                   {chat.messages.length} messages
//                 </span>
//               </div>
//             </motion.div>
//           ))}
//         </div>
//       </motion.div>
//     </motion.div>
//   );
// };

const AIChat = ({ onClose }) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const {
    conversationHistory,
    nlpStatus,
    userLocation,
    locationPermission,
    sessionId,
    chatHistory,
  } = useSelector((state) => state.aiNlp);

  // Load chat history on mount
  // useEffect(() => {
  //   dispatch(fetchChatHistoryThunk());
  // }, [dispatch]);

  // Initialize session ID on mount if not exists
  useEffect(() => {
    if (!sessionId) {
      const newSessionId = uuidv4();
      dispatch(setSessionId(newSessionId));
    }
  }, [dispatch, sessionId]);

  useEffect(() => {
    // Request location permission on mount
    if (locationPermission === "pending") {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            dispatch(setLocationPermission("granted"));
            dispatch(
              setUserLocation({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              })
            );
          },
          () => {
            dispatch(setLocationPermission("denied"));
          }
        );
      } else {
        dispatch(setLocationPermission("denied"));
      }
    }
  }, [dispatch, locationPermission]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (messagesEndRef.current) {
      const scrollOptions = {
        behavior: nlpStatus === "loading" ? "auto" : "smooth",
        block: "end",
      };
      messagesEndRef.current.scrollIntoView(scrollOptions);
    }
  }, [conversationHistory, nlpStatus]);

  const handleClose = async () => {
    if (nlpStatus !== "loading") {
      setIsClosing(true);
      if (sessionId) {
        await dispatch(endChatSessionThunk(sessionId));
      }
      setTimeout(() => {
        onClose();
      }, 200);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || nlpStatus === "loading") return;

    const query = inputValue;
    setInputValue("");

    try {
      await dispatch(
        fetchAiNlpPropertiesThunk({
          prompt: query,
          sessionId: sessionId,
          location: userLocation,
        })
      ).unwrap();

      dispatch(
        sendUserActivityLog({
          eventType: "AI_NLP_CHAT_MESSAGE",
          metadata: {
            searchQuery: query,
            userLocation: userLocation,
            sessionId: sessionId,
          },
        })
      );
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleSelectChat = async (selectedSessionId) => {
    try {
      await dispatch(loadChatSessionThunk(selectedSessionId));
      setShowHistory(false);
    } catch (error) {
      console.error("Failed to load chat session:", error);
    }
  };

  const toggleSpeechRecognition = () => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Speech recognition is not supported in your browser.");
      return;
    }

    if (isListening) {
      window.recognition?.stop();
    } else {
      const recognition = new window.webkitSpeechRecognition();
      window.recognition = recognition;

      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    }
  };

  const handleClearChat = async () => {
    try {
      if (sessionId) {
        await dispatch(endChatSessionThunk(sessionId));
      }
      dispatch(clearConversation());
      const newSessionId = uuidv4();
      dispatch(setSessionId(newSessionId));
    } catch (error) {
      console.error("Error clearing chat:", error);
    }
  };

  return (
    <>
      <motion.div
        className="ai-chat-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          opacity: isClosing ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}>
        <div className="ai-chat-header">
          <div className="flex items-center">
            <SparklesIcon className="h-6 w-6 mr-2" />
            <h2 className="ai-chat-title">Armin AI Assistant</h2>
          </div>
          <div className="flex items-center space-x-2">
            {/* <motion.button
              onClick={() => setShowHistory(true)}
              className="ai-chat-history"
              whileHover={{ opacity: 1 }}
              whileTap={{ scale: 0.95 }}>
              <ClockIcon className="h-5 w-5" />
            </motion.button> */}
            <motion.button
              onClick={handleClearChat}
              className="ai-chat-clear"
              whileHover={{ opacity: 1 }}
              whileTap={{ scale: 0.95 }}>
              <ArrowPathIcon className="h-5 w-5" />
            </motion.button>
            <motion.button
              onClick={handleClose}
              className="ai-chat-close"
              whileHover={{ opacity: 1 }}
              whileTap={{ scale: 0.95 }}>
              <XMarkIcon className="h-5 w-5" />
            </motion.button>
          </div>
        </div>

        {locationPermission === "pending" && (
          <motion.div
            className="location-permission"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}>
            To provide better property recommendations, please allow location
            access.
          </motion.div>
        )}

        <div className="ai-chat-messages">
          {/* Welcome message */}
          {conversationHistory.length === 0 && (
            <Message
              role="assistant"
              content={
                <>
                  <p className="text-sm">
                    👋 Hi! I'm your Armin AI assistant. My job is to help you
                    find properties based on your search using AI.
                  </p>
                  <p className="text-sm pt-2">Try asking me things like:</p>
                  <ul className="list-disc list-inside text-sm">
                    <li>
                      "Show me 3-bedroom detached homes under $1,000,000 in
                      Markham"
                    </li>
                    <li>
                      "Find luxury condos with a pool in Downtown Toronto"
                    </li>
                    <li>"What are the newest listings in Toronto?"</li>
                  </ul>
                </>
              }
            />
          )}

          {/* Conversation history */}
          <AnimatePresence>
            {conversationHistory.map((msg, index) => (
              <Message
                key={index}
                role={msg.role}
                content={msg.content}
                properties={
                  msg.role === "assistant" && msg.listings ? msg.listings : null
                }
                error={msg.error}
              />
            ))}

            {/* Loading indicator */}
            {nlpStatus === "loading" && (
              <motion.div
                className="message ai-message"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-2 h-2 bg-[#6E3AFF] rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-[#6E3AFF] rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-[#6E3AFF] rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={messagesEndRef} />
        </div>

        <motion.form
          onSubmit={handleSubmit}
          className="ai-chat-input-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="What are you looking for?"
            className="ai-chat-input"
          />
          <motion.button
            type="button"
            onClick={toggleSpeechRecognition}
            className={`ai-chat-voice ${isListening ? "listening" : ""}`}
            whileHover={{ opacity: 0.9 }}
            whileTap={{ scale: 0.95 }}>
            <MicrophoneIcon className="h-5 w-5" />
          </motion.button>
          <motion.button
            type="submit"
            className="ai-chat-send"
            disabled={nlpStatus === "loading"}
            whileHover={{ opacity: 0.9 }}
            whileTap={{ scale: 0.95 }}>
            <PaperAirplaneIcon className="h-5 w-5" />
          </motion.button>
        </motion.form>
      </motion.div>

      {/* <ChatHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        chatHistory={chatHistory}
        onSelectChat={handleSelectChat}
      /> */}
    </>
  );
};

export default AIChat;
